import {
  FALLBACK_COLUMN_WIDTH,
  type TimelineLegendItem,
  type TimelineUtils,
} from "@/components/Timeline/timelineUtils";

import type { MilestoneStatus } from "./planOfRecordData";

/**
 * Timeline colouring for the plan of record.
 *
 * The shared milestones utils only know "Actual Date" / "Planned Date" / "Forecast Date",
 * so a derived status passed through them gets no colour at all. These map status to the
 * same palette the status badge uses, so a row reads the same in either view.
 */
const STATUS_LABEL: Record<MilestoneStatus, string> = {
  "on-track": "On track",
  "at-risk": "At risk",
  late: "Late",
  complete: "Complete",
};

const FILL: Record<string, string> = {
  "On track": "#DCFCE7",
  "At risk": "#FEF3C7",
  Late: "#FEE2E2",
  Complete: "#D2EFF9",
};

const BORDER: Record<string, string> = {
  "On track": "#15803D",
  "At risk": "#B26500",
  Late: "#B91C1C",
  Complete: "#06709B",
};

const COLUMN_LABELS: Record<string, string> = {
  event_name: "Milestone",
  start_date: "Planned",
  end_date: "Actual",
  event_type: "Status",
  trial: "Scope",
};

const COLUMN_WIDTHS: Record<string, number> = {
  event_name: 220,
  start_date: 120,
  end_date: 120,
};

/** Milestone first, then its dates. Status is carried by the row colour, not a column. */
export const PLAN_OF_RECORD_TIMELINE_COLUMNS = [
  "event_name",
  "start_date",
  "end_date",
] as const;

export const planOfRecordTimelineUtils: TimelineUtils = {
  defaultVisibleColumns: PLAN_OF_RECORD_TIMELINE_COLUMNS,
  columnLabels: COLUMN_LABELS,
  getTableColumnWidth: (column) =>
    COLUMN_WIDTHS[column] ?? FALLBACK_COLUMN_WIDTH,
  getLegendFillColor: (value) => FILL[value] ?? "",
  getLegendBorderColor: (value) => BORDER[value] ?? "",
  getLegendBorderColorFromFill: (fillColor) => {
    const match = Object.keys(FILL).find(
      (key) => FILL[key].toLowerCase() === fillColor.toLowerCase(),
    );
    return match ? BORDER[match] : "";
  },
  getAllLegendItems: (keys?: string[]): TimelineLegendItem[] => {
    const all = Object.values(STATUS_LABEL);
    const selected = keys?.length
      ? keys.filter((key) => all.includes(key))
      : all;
    return selected.map((key) => ({
      key,
      label: key,
      borderColor: BORDER[key],
      fillColor: FILL[key],
    }));
  },
};

export { STATUS_LABEL as TIMELINE_STATUS_LABEL };
